<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import clubInvitationService from '../services/clubInvitation.service'
import { useClubInvitationStatus } from '../composables/useClubInvitationStatus'
import type { ClubInvitationDetail, ClubInvitationEvent } from '../types/clubInvitation.types'

const props = defineProps<{
  show: boolean
  clubUuid: string
  invitationUuid: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()
const { statusLabel, statusBadgeClass, eventLabel, eventDotClass, formatDateTime } = useClubInvitationStatus()

const invitation = ref<ClubInvitationDetail | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const fetchInvitation = async () => {
  if (!props.clubUuid || !props.invitationUuid) return
  loading.value = true
  error.value = null
  invitation.value = null
  try {
    invitation.value = await clubInvitationService.getInvitation(props.clubUuid, props.invitationUuid)
  } catch (err: any) {
    console.error('Fetch invitation error:', err)
    error.value = err.response?.data?.message || t('clubInvitations.errorFetchingOne')
  } finally {
    loading.value = false
  }
}

watch(() => [props.show, props.invitationUuid], () => {
  if (props.show && props.invitationUuid) fetchInvitation()
})

const close = () => emit('close')

const personLine = (event: ClubInvitationEvent): string | null => {
  if (!event.person_name) return null
  if (event.type === 'accepted' || event.type === 'rejected') {
    return t('clubInvitations.eventReviewedBy', { name: event.person_name })
  }
  if (event.type === 'queued' || event.type === 'sent' || event.type === 'failed') {
    return t('clubInvitations.eventSentBy', { name: event.person_name })
  }
  return null
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="close"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invitation-history-title"
    >
      <div class="p-6">
        <h2 id="invitation-history-title" class="text-xl font-bold text-gray-900 mb-1">{{ t('clubInvitations.historyTitle') }}</h2>

        <div v-if="loading" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <div v-else-if="error" class="mt-3 p-3 bg-red-50 border-2 border-red-300 rounded text-sm text-red-800">
          {{ error }}
        </div>

        <template v-else-if="invitation">
          <p class="text-sm font-semibold text-gray-900 break-all">{{ invitation.email }}</p>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <span :class="['inline-block px-2 py-0.5 text-xs font-medium rounded', statusBadgeClass(invitation.status)]">
              {{ statusLabel(invitation) }}
            </span>
            <span v-if="invitation.status_at" class="text-xs text-gray-500 tabular-nums">{{ formatDateTime(invitation.status_at) }}</span>
          </div>

          <!-- Summary -->
          <dl class="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div class="p-2 bg-gray-50 rounded border border-gray-200">
              <dt class="text-xs text-gray-600">{{ t('clubInvitations.colLastSent') }}</dt>
              <dd class="font-medium text-gray-900 tabular-nums">{{ formatDateTime(invitation.last_sent_at) }}</dd>
            </div>
            <div class="p-2 bg-gray-50 rounded border border-gray-200">
              <dt class="text-xs text-gray-600">{{ t('clubInvitations.summarySendCount') }}</dt>
              <dd class="font-medium text-gray-900">{{ invitation.send_count }}</dd>
            </div>
            <div class="p-2 bg-gray-50 rounded border border-gray-200">
              <dt class="text-xs text-gray-600">{{ t('clubInvitations.summaryOpens') }}</dt>
              <dd class="font-medium text-gray-900">{{ invitation.open_count }}</dd>
            </div>
            <div class="p-2 bg-gray-50 rounded border border-gray-200">
              <dt class="text-xs text-gray-600">{{ t('clubInvitations.summaryClicks') }}</dt>
              <dd class="font-medium text-gray-900">{{ invitation.click_count }}</dd>
            </div>
            <div class="p-2 bg-gray-50 rounded border border-gray-200">
              <dt class="text-xs text-gray-600">{{ t('clubInvitations.summaryApplications') }}</dt>
              <dd class="font-medium text-gray-900">{{ invitation.applications_count }}</dd>
            </div>
            <div class="p-2 bg-gray-50 rounded border border-gray-200">
              <dt class="text-xs text-gray-600">{{ t('clubInvitations.colSentBy') }}</dt>
              <dd class="font-medium text-gray-900 truncate">{{ invitation.sent_by?.name || '-' }}</dd>
            </div>
          </dl>

          <div
            v-if="invitation.delivery_status === 'failed' && invitation.last_error"
            class="mt-3 p-3 bg-red-50 border-2 border-red-300 rounded text-xs text-red-800 break-words"
          >
            <span class="font-semibold">{{ t('clubInvitations.eventError') }}</span> {{ invitation.last_error }}
          </div>

          <!-- Timeline -->
          <h3 class="mt-5 mb-3 text-sm font-semibold text-gray-900">{{ t('clubInvitations.historyEvents') }}</h3>
          <p v-if="invitation.events.length === 0" class="text-sm text-gray-500">{{ t('clubInvitations.historyEmpty') }}</p>
          <ol v-else class="ml-1.5 border-l-2 border-gray-200 space-y-4">
            <li
              v-for="(event, index) in invitation.events"
              :key="`${event.type}-${event.occurred_at}-${index}`"
              class="relative pl-5"
            >
              <span
                :class="['absolute -left-[7px] top-1.5 w-3 h-3 rounded-full ring-2 ring-white', eventDotClass(event.type)]"
                aria-hidden="true"
              ></span>
              <div class="flex flex-wrap items-baseline justify-between gap-x-3">
                <span class="text-sm font-medium text-gray-900">{{ eventLabel(event.type) }}</span>
                <time :datetime="event.occurred_at" class="text-xs text-gray-500 tabular-nums">{{ formatDateTime(event.occurred_at) }}</time>
              </div>
              <p v-if="event.application" class="text-xs text-gray-600 mt-0.5">
                {{ t('clubInvitations.eventPlayer') }}
                <router-link
                  :to="`/club-applications/${event.application.uuid}`"
                  class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >{{ event.application.player_name }}</router-link>
              </p>
              <p v-if="personLine(event)" class="text-xs text-gray-600 mt-0.5">{{ personLine(event) }}</p>
              <p v-if="event.error" class="text-xs text-red-700 mt-0.5 break-words">
                <span class="font-semibold">{{ t('clubInvitations.eventError') }}</span> {{ event.error }}
              </p>
            </li>
          </ol>
        </template>

        <div class="mt-6 flex justify-end">
          <button
            @click="close"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
          >
            {{ t('clubApplications.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
